/**
 * Multi-door stores (owner-approved feature, TZ FR-RB-02): the store total plus a per-door breakdown and the
 * store's coverage state (full / partial / none). The client only ever sees door names, never camera data.
 *
 * This module is the single place that reads the door fields from API answers. The shapes are aliases of the
 * generated OpenAPI schemas (`DoorMetrics`, `DoorOutage`, `DownDoor`, `StoreCoverage`, the door fields of
 * `MetricsResult` and `LiveSnapshot`); nothing here is written by hand.
 */
import type { components } from './schema/generated_schema';
import type { LiveEvent, MetricsResponse } from './api_types';

type Schema<SchemaName extends keyof components['schemas']> = components['schemas'][SchemaName];

export type CoverageState = Schema<'StoreCoverage'>['coverage_state'];

export const COVERAGE_STATES: readonly CoverageState[] = ['full', 'partial', 'none'];

/** One stretch of time when a door was not counted; `ends_at` null = still going on. */
export type DoorOutage = Schema<'DoorOutage'>;

export type DoorMetrics = Schema<'DoorMetrics'>;

/** A door that is down right now (store live / status answer). */
export type DownDoor = Schema<'DownDoor'>;

/** Store-level door fields of the metrics answer for one store. */
export interface StoreDoorMetricsFields {
  doors: DoorMetrics[];
  coverage_state: CoverageState;
  coverage_pct: number | null;
}

/** Per-store coverage of a multi-store metrics answer (dashboard tiles), keyed by store id. */
export type StoreCoverageEntry = Schema<'StoreCoverage'>;

export interface StoreCoverageMapFields {
  store_coverage: Record<string, StoreCoverageEntry>;
}

/** Store live / status fields. */
export interface StoreLiveStatusFields {
  coverage_state: CoverageState;
  down_doors: DownDoor[];
}

/** Door breakdown and coverage of one store, as the store page shows it. */
export interface StoreDoorSummary {
  doors: readonly DoorMetrics[];
  coverageState: CoverageState | null;
  coveragePct: number | null;
}

export function isCoverageState(candidate: unknown): candidate is CoverageState {
  return typeof candidate === 'string' && (COVERAGE_STATES as readonly string[]).includes(candidate);
}

/** Reads the door fields of a single-store metrics answer; null when the answer has none (older backend). */
export function storeDoorSummaryOf(metrics: MetricsResponse | undefined): StoreDoorSummary | null {
  if (metrics === undefined) {
    return null;
  }
  const coverageState = isCoverageState(metrics.coverage_state) ? metrics.coverage_state : null;
  if ((metrics.doors === undefined || metrics.doors === null) && coverageState === null) {
    return null;
  }
  return {
    doors: metrics.doors ?? [],
    coverageState,
    coveragePct: metrics.coverage_pct ?? null,
  };
}

/** Coverage of one store from a multi-store metrics answer (dashboard tiles); null when not reported. */
export function storeCoverageOf(
  metrics: MetricsResponse | undefined,
  storeId: string,
): StoreCoverageEntry | null {
  if (metrics === undefined) {
    return null;
  }
  const coverageEntry = metrics.store_coverage?.[storeId];
  return coverageEntry !== undefined && isCoverageState(coverageEntry.coverage_state) ? coverageEntry : null;
}

/** Live status of a store from a live event; null when the event carries no door state. */
export function liveStatusOf(liveEvent: LiveEvent | undefined): StoreLiveStatusFields | null {
  if (liveEvent === undefined) {
    return null;
  }
  if (!isCoverageState(liveEvent.coverage_state)) {
    return null;
  }
  return { coverage_state: liveEvent.coverage_state, down_doors: liveEvent.down_doors ?? [] };
}

/** The well-formed down doors of a parsed live payload (anything else is dropped). */
export function downDoorsOf(candidate: unknown): DownDoor[] {
  if (!Array.isArray(candidate)) {
    return [];
  }
  return candidate.filter(
    (entry: unknown): entry is DownDoor =>
      typeof entry === 'object' &&
      entry !== null &&
      typeof (entry as Partial<DownDoor>).door_name === 'string' &&
      typeof (entry as Partial<DownDoor>).since === 'string' &&
      typeof (entry as Partial<DownDoor>).store_id === 'string',
  );
}

/** The door section and per-door figures are shown only for stores with two or more doors. */
export const MINIMUM_DOORS_FOR_BREAKDOWN = 2;

export function hasDoorBreakdown(summary: StoreDoorSummary | null): summary is StoreDoorSummary {
  return summary !== null && summary.doors.length >= MINIMUM_DOORS_FOR_BREAKDOWN;
}

/** Share of each door in the store total, 0..100 (null when the store total is zero or unknown). */
export function doorSharePercent(door: DoorMetrics, doors: readonly DoorMetrics[]): number | null {
  const storeTotal = doors.reduce((sum, anyDoor) => sum + (anyDoor.visits ?? 0), 0);
  if (storeTotal <= 0 || door.visits === null) {
    return null;
  }
  return (door.visits / storeTotal) * 100;
}
